import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import uniqBy from 'lodash/uniqBy';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../../../context/PortalContext';
import { getPlanSelectorVariables } from '../../../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { VirtualCareProviderModal } from '../../../../frontends/ProviderSearch/routes/MapView/VirtualCareProvidersBanner/VirtualCareProvidersModal';
import { ProgramPromotionModal } from '../../../../frontends/ProviderSearch/routes/ProgramPromotionSection/ProgramPromotionModal';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../hooks/useLagoon';
import { useCeOffersStore } from '../../../../store/useCeOffersStore';
import { useChipStore } from '../../../../store/useChipStore';
import { ChipState } from '../../../../store/useChipStore/chipStore';
import { useVirtualCareStore } from '../../../../store/useVirtualCareStore';
import { shouldUseGehaV2Styling } from '../../../../utils/gehaThemeUtils';
import { getDetailsPathByTypeaheadType } from '../../../../utils/providerDetails.utils';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import { getCurrentMember } from '../../../../utils/user.utils';
import { adobeLinkTrackEvent } from '../../../AdobeTagging/adobeLinkTrackEvent';
import {
  GYMBENEFITROLLUPCODE,
  ReverseDentalCodes,
  SPECIALITYROLLUPCODEICARE,
  TypeaheadBHType,
  TypeaheadProviderTypes,
  UESSearchType,
} from '../../../Constants';
import { ConstantsLagoon } from '../../../ConstantsLagoon';
import { PortalIdentifier } from '../../../Portal';
import { PSXIcon } from '../../../PSXIcon';
import { convertObjectToUrlParams } from '../../../Utils';
import {
  checkVisionCoverageTypeAhead,
  getMpCodes,
  getProgramPromotionsForTypeahead,
  getUniqueVirtualCareProviders,
  getVirtualCareProvidersForTypeaheadFromEligibilities,
  isMNRHbpbCodePresent,
} from '../utils';
import { DisplayCoverageTypes } from './DisplayCoverageTypes';
import GuestDropdownBeforeMessage from './GuestDropdownBeforeMessage';
import { GymBenefitsList } from './GymBenefitsList';
import {
  Divider,
  DropdownContainer,
  ICareListItem,
  ProviderIcon,
  SuggestionListContainer,
  SuggestionListItem,
  SuggestionSectionItem,
  isPrimaryActiveSuggestion,
} from './KeywordSearchDropdown.styled';
import { ProgramPromotionList } from './ProgramPromotionList';
import { SuggestionItem } from './SuggestionItem';
import { UserTextSearch } from './UserTextSearch';
import { VirtualCareProvidersList } from './VirtualCareProvidersList';

const MAX_NPI_LICENSE_AND_PHONE_NUMBER_RESULTS = 3;
interface ProviderItem {
  providerType: string;
}
type Props = {
  typeAheadSuggestions;
  onSuggestionClick;
  activeSuggestion;
  onMoreResultClick: (item: ProviderItem, searchType: string) => void;
  getCoverageType;
  keywordSearchTerm;
  memberDDP;
  sortItems;
  hideDisabledTypeahead;
  handleGeneralSearchClick;
  isLLMGeneralSearchEnabled;
  specialityRollUpCodes;
};
export const SuggestionList = ({
  typeAheadSuggestions,
  onSuggestionClick,
  onMoreResultClick,
  activeSuggestion,
  getCoverageType,
  keywordSearchTerm,
  memberDDP,
  sortItems,
  hideDisabledTypeahead,
  isLLMGeneralSearchEnabled,
  handleGeneralSearchClick,
  specialityRollUpCodes = '',
}: Props) => {
  const lagoonData = useLagoon('ui-messaging')();
  const allConfigs = useLagoon('config');
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProviderName, setSelectedProviderName] = useState<
    string | null
  >(null);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [selectedOfferName, setSelectedOfferName] = useState<string | null>(
    null
  );

  const hbpbCodes = (
    getLagoonConfigValue(allConfigs, 'VISION_NON_UHC_ICARE') || ''
  ).split(',');
  const currentMember = getCurrentMember();
  const { planToken } = getPlanSelectorVariables();
  const uesVirtualCareKeywordMappingTable = useLagoon(
    'ues-virtual-care-keyword-mapping'
  )();
  const programPromotionsRollupCodesTable = useLagoon(
    'program-promotions-pseudo-rollupcode-mapping'
  )();
  const enablePseudoRollupCodeOnlyMatch = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PSEUDO_ROLLUP_CODE_ONLY_MATCH'
  );
  const enableProgramPromotionInTypeahead = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PROGRAM_PROMOTION_IN_TYPEAHEAD'
  );

  const enableVirtualCareNameSearchPsxLogic = getLagoonConfigValue(
    allConfigs,
    'ENABLE_VIRTUAL_CARE_NAME_SEARCH_PSX_LOGIC'
  );

  const virtualCareNameSearchAlternatives = getLagoonConfigValue(
    allConfigs,
    'VIRTUAL_CARE_NAME_SEARCH_ALTERNATIVES'
  );

  const isMemberGymBenefitEligible =
    currentMember?.fitnessCoverageEligibility === 'Y';

  const isGymBenefitEligible =
    isMemberGymBenefitEligible &&
    specialityRollUpCodes?.includes(GYMBENEFITROLLUPCODE);

  const formatSpecialty = (specialtiesList = []) => {
    const distinctSpecialty = uniqBy(specialtiesList, 'category');
    const list = distinctSpecialty.slice(0, 2);
    let displaySpecialty = '';
    list.map((item: any, index: number) => {
      displaySpecialty += `${item.category}${
        index !== list.length - 1 ? ', ' : ''
      }`;
      return displaySpecialty;
    });
    if (distinctSpecialty.length > 2) {
      displaySpecialty += ` (${distinctSpecialty.length - 2} ${t('More')})`;
    }

    return displaySpecialty;
  };

  const eligibilities = useVirtualCareStore((state) => state.eligibilities);
  const ceOffersData = useCeOffersStore((state) => state.ceOffersData);
  const virtualCareProviders =
    getVirtualCareProvidersForTypeaheadFromEligibilities({
      suggestion: typeAheadSuggestions,
      uesVirtualCareKeywordMappingTable,
      eligibilities: eligibilities[planToken],
      enablePseudoRollupCodeOnlyMatch,
      enableVirtualCareNameSearchPsxLogic,
      keywordSearchTerm,
      virtualCareNameSearchAlternatives,
    });
  const mpCodes = getMpCodes(
    specialityRollUpCodes,
    programPromotionsRollupCodesTable
  );
  const programPromotions = getProgramPromotionsForTypeahead(
    ceOffersData,
    mpCodes
  );

  const uniqueVirtualCareProviders =
    getUniqueVirtualCareProviders(virtualCareProviders);
  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 ? isGEHAV2 : isGeha;

  const hospitalIcon = isGehaTheme ? 'geha_hospital' : 'hospital';

  const [
    enableRightClickMenu,
    isDentalLocationNameEnable,
    isTypeaheadBehavioralHealthTagSuppressed,
    isVisionNonUhcMnr,
    enableVirtualCareEpic,
    enableVirtualCareProvidersInTypeahead,
    showGuestTypeaheadCoverageLink,
    enableAutocompleteTaggingChanges,
    enableMapGymBenefits,
    enableProgramPromotion,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_RIGHT_CLICK_MENU,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_DENTAL_LOCATION_NAME,
    ConstantsLagoon.FEATURE_FLAGS.SUPPRESS_TYPEAHEAD_BH_TAG,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VISION_FOR_NON_UHC_MNR,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_EPIC,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_PROVIDERS_IN_TYPEAHEAD,
    ConstantsLagoon.FEATURE_FLAGS.GUEST_TYPEAHEAD_COVERAGE_LINK,
    ConstantsLagoon.FEATURE_FLAGS.AUTOCOMPLETE_TAGGING_CHANGES,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAPGYM_BENEFITS,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROGRAM_PROMOTION,
  ]);
  const icareLagoonContent = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.ICARE_TYPE_AHEAD_SEARCH_CONTENT,
  });
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const getProviderDetailsUrl = (item) => {
    const paramsObj = {
      providerId: item?.providerId,
      locationId: item?.locationId,
      coverageType: item?.coverageType?.[0],
    };
    const params = convertObjectToUrlParams(chipStore, paramsObj);
    const baseUrl = getDetailsPathByTypeaheadType(item?.providerType);
    if (
      item?.providerType === TypeaheadProviderTypes.FACILITY ||
      item?.providerType === TypeaheadProviderTypes.PRACTITIONER ||
      item?.providerType === TypeaheadProviderTypes.PROVIDER_GROUP
    ) {
      const url = `/findcare${baseUrl}${params}`;
      return url;
    }
    return '';
  };

  const handleLinkTrackEvent = () => {
    if (icareLagoonContent) {
      const analyticsInfo = {
        name: icareLagoonContent?.icon,
        location: 'body:search input option list:about vision coverage',
        destinationUrl: icareLagoonContent?.url,
      };
      adobeLinkTrackEvent({
        ...analyticsInfo,
      });
    }
  };

  return (
    <React.Fragment>
      <Divider />
      <SuggestionListContainer
        data-auto-testid="suggestion-list-container"
        data-testid="suggestion-list-containter"
      >
        <DropdownContainer
          data-testid="primary-dropdown-container"
          role="listbox"
        >
          <GuestDropdownBeforeMessage show={showGuestTypeaheadCoverageLink} />

          {isLLMGeneralSearchEnabled &&
            !typeAheadSuggestions?.some(
              (item) =>
                item?.suggestion &&
                item?.suggestion?.toLowerCase() ===
                  keywordSearchTerm?.toLowerCase()
            ) &&
            !typeAheadSuggestions?.some(
              (item) =>
                item?.section &&
                item?.items?.find(
                  (value) =>
                    value?.displayName?.toLowerCase() ===
                    keywordSearchTerm?.toLowerCase()
                )
            ) && (
              <SuggestionSectionItem
                className={`first-child ${activeSuggestion === 0}`}
                role="option"
              >
                <UserTextSearch
                  data-testid="user-text-search-common-suggestion"
                  key={keywordSearchTerm}
                  keywordSearchTerm={keywordSearchTerm}
                  onClick={handleGeneralSearchClick}
                />
              </SuggestionSectionItem>
            )}
          {typeAheadSuggestions?.length > 0 &&
            typeAheadSuggestions?.sort(sortItems).map((value, index) => {
              if (
                value?.suggestion &&
                !value?.pseudoVerticals?.includes(SPECIALITYROLLUPCODEICARE)
              ) {
                return (
                  <SuggestionItem
                    activeSuggestion={activeSuggestion}
                    data-testid="search-input-box-typeahead-suggestion"
                    getCoverageType={getCoverageType}
                    hideDisabledTypeahead={hideDisabledTypeahead}
                    index={index}
                    key={value.id}
                    keywordSearchTerm={keywordSearchTerm}
                    memberDDP={memberDDP}
                    onClick={() => {
                      onSuggestionClick(value, index);
                    }}
                    value={value}
                  />
                );
              }
              if (value?.section) {
                const items = value?.items
                  ?.filter((_, index) => {
                    if (
                      [
                        UESSearchType.PhoneNumber,
                        UESSearchType.NpiNumber,
                        UESSearchType.LicenseNumber,
                      ].includes(value?.uesSearchType)
                    ) {
                      return index < MAX_NPI_LICENSE_AND_PHONE_NUMBER_RESULTS;
                    }
                    return true;
                  })
                  .sort(sortItems);

                return items.map((item, ix) => {
                  const speciality = formatSpecialty(item.speciality);
                  const url = getProviderDetailsUrl(item);
                  const hrefOptions = enableRightClickMenu
                    ? { href: url, as: 'a' }
                    : {};
                  return (
                    <>
                      {ix === 0 && <Divider />}
                      <SuggestionSectionItem
                        aria-posinset={ix + typeAheadSuggestions.length}
                        className={`${
                          activeSuggestion ===
                          ix + typeAheadSuggestions.length - 1
                            ? 'active'
                            : ''
                        } ${ix === 0 ? 'first-child' : ''}`}
                        id={
                          activeSuggestion ===
                          ix + typeAheadSuggestions.length - 1
                            ? 'primary-search-bar-result-suggestion'
                            : ''
                        }
                        key={`${value.section}-${speciality}-${item.displayName}-${item.displayAddress}`}
                        role="option"
                      >
                        {ix === 0 && (
                          <>
                            {typeAheadSuggestions?.[index - 1]?.items
                              ?.length ? (
                              <Divider />
                            ) : (
                              ''
                            )}
                            <Text
                              color="$gray8"
                              data-testid="heading-typeahead-suggested-searches"
                              size="14.22px"
                            >
                              <strong>{value.section}</strong>
                              {[
                                UESSearchType.PhoneNumber,
                                UESSearchType.NpiNumber,
                                UESSearchType.LicenseNumber,
                              ].includes(value.uesSearchType) ? (
                                <>
                                  <span>{` ${t('matching')} `} </span>
                                  <strong>"{keywordSearchTerm}"</strong>
                                </>
                              ) : null}
                            </Text>
                          </>
                        )}
                        <SuggestionListItem
                          className="suggestion-link-span"
                          color="$interactive1"
                          css={
                            item?.coverageType?.some((coverageCode) =>
                              getCoverageType.includes(coverageCode)
                            )
                              ? {
                                  '&.abyss-flex-root':
                                    activeSuggestion ===
                                    ix + typeAheadSuggestions.length - 1
                                      ? isPrimaryActiveSuggestion
                                      : '',
                                }
                              : { pointerEvents: 'none' }
                          }
                          onClick={(ev) => {
                            ev.stopPropagation();
                            ev.preventDefault();
                            onSuggestionClick(item, ix);
                          }}
                          {...hrefOptions}
                        >
                          <div style={{ display: 'flex' }}>
                            <div>
                              {item?.providerType ===
                                TypeaheadProviderTypes.PROVIDER_GROUP ||
                              (item?.providerType ===
                                TypeaheadProviderTypes.BEHAVIORAL_HEALTH &&
                                item?.providerId?.startsWith(
                                  TypeaheadBHType.GRP
                                )) ? (
                                <IconMaterial
                                  data-testid="providergroup-icon"
                                  icon="people"
                                  style={ProviderIcon}
                                />
                              ) : item?.providerType ===
                                TypeaheadProviderTypes.PRACTITIONER ? (
                                <IconMaterial
                                  data-testid="provider-icon"
                                  icon="person"
                                  style={ProviderIcon}
                                />
                              ) : item?.providerType ===
                                  TypeaheadProviderTypes.FACILITY ||
                                item?.providerType ===
                                  TypeaheadProviderTypes.MEDICAL_HEALTH_SUPPLIES ||
                                (item?.providerType ===
                                  TypeaheadProviderTypes.BEHAVIORAL_HEALTH &&
                                  item?.providerId?.startsWith(
                                    TypeaheadBHType.FAC
                                  )) ? (
                                <PSXIcon
                                  data-testid="facility-icon"
                                  icon={hospitalIcon}
                                  style={ProviderIcon}
                                />
                              ) : (
                                ''
                              )}
                            </div>
                            <Text
                              color="$gray7"
                              data-testid="search-input-box-typeahead-suggestion-section"
                              fontWeight={500}
                              size="14.22px"
                            >
                              <Text
                                color={
                                  !item?.coverageType?.some((coverageCode) =>
                                    getCoverageType.includes(coverageCode)
                                  )
                                    ? '$gray6'
                                    : '$info1'
                                }
                                data-testid="typeahead-suggestions"
                                fontWeight={700}
                              >
                                {t(item?.displayName)}
                              </Text>
                              {isDentalLocationNameEnable &&
                              item?.coverageType?.some((coverageCode) =>
                                ReverseDentalCodes.includes(coverageCode)
                              ) ? (
                                <Text
                                  style={{
                                    marginLeft: '8px',
                                    fontWeight: 600,
                                  }}
                                >
                                  {item?.locationName}
                                </Text>
                              ) : (
                                ''
                              )}
                              <Text
                                style={{
                                  marginLeft: '8px',
                                  marginRight: '8px',
                                }}
                              >
                                {speciality}
                              </Text>
                              <Text style={{ display: 'inline-block' }}>
                                <DisplayCoverageTypes
                                  enableAutocompleteTaggingChanges={
                                    enableAutocompleteTaggingChanges
                                  }
                                  getCoverageType={getCoverageType}
                                  hideDisabledTypeahead={false}
                                  isTypeaheadBehavioralHealthTagSuppressed={
                                    isTypeaheadBehavioralHealthTagSuppressed
                                  }
                                  item={item}
                                  memberDDP={memberDDP}
                                />
                              </Text>
                            </Text>
                          </div>
                        </SuggestionListItem>
                      </SuggestionSectionItem>
                      {[
                        UESSearchType.PhoneNumber,
                        UESSearchType.NpiNumber,
                        UESSearchType.LicenseNumber,
                      ].includes(value?.uesSearchType) &&
                      value?.items?.length >
                        MAX_NPI_LICENSE_AND_PHONE_NUMBER_RESULTS &&
                      ix === items.length - 1 ? (
                        <Link
                          data-testid="button-more-results"
                          onClick={() => {
                            onMoreResultClick(item, value?.uesSearchType);
                          }}
                          size="sm"
                        >
                          {t('more-results')}
                        </Link>
                      ) : null}
                    </>
                  );
                });
              }
              return null;
            })}
          {isVisionNonUhcMnr &&
            ((isMNRHbpbCodePresent(currentMember, hbpbCodes) &&
              typeAheadSuggestions.length > 0 &&
              checkVisionCoverageTypeAhead(typeAheadSuggestions)) ||
              (isMNRHbpbCodePresent(currentMember, hbpbCodes) &&
                specialityRollUpCodes === SPECIALITYROLLUPCODEICARE)) && (
              <>
                {typeAheadSuggestions.length > 0 ? <Divider /> : ''}
                <SuggestionSectionItem
                  className="first-child"
                  id=""
                  role="option"
                >
                  <Text
                    color="$gray8"
                    data-testid="heading-typeahead-suggested-searches"
                    fontWeight={700}
                    size="14.22px"
                  >
                    {icareLagoonContent && icareLagoonContent.title}
                  </Text>
                  <ICareListItem
                    className="suggestion-link-span"
                    color="$interactive1"
                    data-testid="search-input-box-typeahead-suggestion-section"
                  >
                    <div style={{ display: 'flex' }}>
                      {icareLagoonContent && (
                        <Link
                          data-testid="go-to-icare-link"
                          href={icareLagoonContent?.url}
                          onClick={handleLinkTrackEvent}
                        >
                          {icareLagoonContent.icon}
                        </Link>
                      )}
                      <Text
                        style={{
                          marginLeft: '8px',
                          marginRight: '8px',
                        }}
                      >
                        {icareLagoonContent && icareLagoonContent.message}
                      </Text>
                    </div>
                  </ICareListItem>
                </SuggestionSectionItem>
              </>
            )}
          {enableVirtualCareEpic &&
            enableVirtualCareProvidersInTypeahead &&
            uniqueVirtualCareProviders?.length > 0 && (
              <VirtualCareProvidersList
                providers={uniqueVirtualCareProviders}
                setIsModalOpen={setIsModalOpen}
                setSelectedProviderName={setSelectedProviderName}
              />
            )}
          {enableMapGymBenefits && isGymBenefitEligible && <GymBenefitsList />}
          {enableProgramPromotion &&
            enableProgramPromotionInTypeahead === 'Y' &&
            programPromotions?.length > 0 && (
              <ProgramPromotionList
                keywordSearchTerm={keywordSearchTerm}
                programPromotions={programPromotions}
                setIsOfferModalOpen={setIsOfferModalOpen}
                setSelectedOfferName={setSelectedOfferName}
              />
            )}
          <VirtualCareProviderModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            selectedProviderName={selectedProviderName}
            virtualCareProviders={virtualCareProviders}
          />
          <ProgramPromotionModal
            isOpen={isOfferModalOpen}
            keywordSearchTerm={keywordSearchTerm}
            onClose={() => setIsOfferModalOpen(false)}
            programPromotions={programPromotions}
            selectedOfferName={selectedOfferName}
          />
        </DropdownContainer>
      </SuggestionListContainer>
    </React.Fragment>
  );
};
